import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import SearchableSelect from "./SearchableSelect";
import { confirmDialog } from "../utils/confirmDialog";
import {
  fetchClassifications,
  upsertClassification,
  deleteClassification,
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  fetchSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  fetchOffers,
  createOffer,
  updateOffer,
  deleteOffer,
  fetchUnits,
  upsertUnit,
  bulkUnits,
  bulkClassifications,
  bulkProducts,
  bulkSuppliers,
  bulkOffers,
} from "../api/resourceCatalogApi";
import {
  writeUnitsToExcel, readUnitsFromExcel,
  writeClassificationsToExcel, readClassificationsFromExcel,
  writeProductsToExcel, readProductsFromExcel,
  writeSuppliersToExcel, readSuppliersFromExcel,
  writeOffersToExcel, readOffersFromExcel,
} from "../excel/resourceCatalogSheets";

const KINDS = [
  { id: "MATERIAL", label: "მასალა / Material", color: "#4CAF50" },
  { id: "EQUIPMENT", label: "მექანიზმი / Equipment", color: "#FF9800" },
  { id: "LABOUR", label: "ხელფასი / Labour", color: "#2196F3" },
];

const SUBTABS = [
  { id: "catalog", label: "კატალოგი / Catalog" },
  { id: "suppliers", label: "მომწოდებლები / Suppliers" },
  { id: "offers", label: "ფასები / Prices" },
  { id: "units", label: "ერთეულები / Units" },
];

export default function ResourceCatalogPanel({ token, isSuperuser = false, editableKinds = [], initialKind }) {
  const canEditAnyKind = isSuperuser || editableKinds.length > 0;
  const [subTab, setSubTab] = useState("catalog");
  const [busy, setBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const clearMsg = () => { setStatusMessage(""); setErrorMessage(""); };

  return (
    <div>
      {/* Sub-tabs */}
      <div className="sheet-lookup-chip-row" style={{ marginBottom: 8 }}>
        {SUBTABS.map((t) => (
          <button
            key={t.id}
            onClick={() => { setSubTab(t.id); clearMsg(); }}
            className={`sheet-lookup-chip ${subTab === t.id ? "active" : ""}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {errorMessage && <div className="error" style={{ marginBottom: 8 }}>{errorMessage}</div>}
      {statusMessage && <div className="status" style={{ marginBottom: 8 }}>{statusMessage}</div>}

      {subTab === "catalog" && (
        <CatalogSubPanel
          token={token}
          canEditAll={isSuperuser}
          editableKinds={editableKinds}
          busy={busy}
          setBusy={setBusy}
          setStatus={setStatusMessage}
          setErr={setErrorMessage}
          initialKind={initialKind}
        />
      )}
      {subTab === "suppliers" && (
        <SuppliersSubPanel
          token={token}
          canEdit={canEditAnyKind}
          busy={busy}
          setBusy={setBusy}
          setStatus={setStatusMessage}
          setErr={setErrorMessage}
        />
      )}
      {subTab === "units" && (
        <UnitsSubPanel
          token={token}
          canEdit={isSuperuser}
          busy={busy}
          setBusy={setBusy}
          setStatus={setStatusMessage}
          setErr={setErrorMessage}
        />
      )}
      {subTab === "offers" && (
        <OffersSubPanel
          token={token}
          canEdit={canEditAnyKind}
          busy={busy}
          setBusy={setBusy}
          setStatus={setStatusMessage}
          setErr={setErrorMessage}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// CATALOG sub-panel: classifications tree + products inside selected node
// ────────────────────────────────────────────────────────────────────

function CatalogSubPanel({ token, canEditAll, editableKinds, busy, setBusy, setStatus, setErr, initialKind }) {
  const [kind, setKind] = useState(initialKind || "MATERIAL");
  const canEdit = canEditAll || editableKinds.includes(kind);
  const [roots, setRoots] = useState([]);
  const [searchClass, setSearchClass] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = tree mode, array = flat search mode
  const [treeKey, setTreeKey] = useState(0); // bump to force the whole tree to reload

  const [showAddRoot, setShowAddRoot] = useState(false);
  const [newClass, setNewClass] = useState({ code: "", name_geo: "", name_eng: "", name_rus: "" });

  const [showAddProduct, setShowAddProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null); // null or product object

  const loadRoots = useCallback(async () => {
    try {
      const list = await fetchClassifications(token, { kind, parentCode: "__root__" });
      setRoots(list || []);
    } catch (e) {
      setErr(e?.message || "Failed to load classifications");
    }
  }, [token, kind, setErr]);

  useEffect(() => { loadRoots(); }, [loadRoots, treeKey]);

  // Debounced search across the whole kind (flat). Empty query → tree mode.
  useEffect(() => {
    const q = searchClass.trim();
    if (!q) { setSearchResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const list = await fetchClassifications(token, { kind, parentCode: "__all__", q });
        setSearchResults(list || []);
      } catch (e) {
        setErr(e?.message || "Search failed");
      }
    }, 250);
    return () => clearTimeout(t);
  }, [searchClass, kind, token, setErr]);

  const reloadTree = () => setTreeKey((k) => k + 1);

  const handleAddRoot = async () => {
    if (!newClass.code.trim()) return setErr("Code required");
    setBusy(true);
    try {
      await upsertClassification(token, {
        code: newClass.code.trim(),
        kind,
        level: 1,
        parent_code: null,
        name_geo: newClass.name_geo || null,
        name_eng: newClass.name_eng || null,
        name_rus: newClass.name_rus || null,
      });
      setShowAddRoot(false);
      setNewClass({ code: "", name_geo: "", name_eng: "", name_rus: "" });
      setStatus(`Root classification ${newClass.code} created.`);
      reloadTree();
    } catch (e) {
      setErr(e?.message || "Failed to add classification");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteClass = async (cl) => {
    if (!(await confirmDialog(`Delete classification "${cl.code}" and all its children + products?`))) return;
    setBusy(true);
    try {
      await deleteClassification(token, cl.code);
      setStatus(`Deleted ${cl.code}.`);
      reloadTree();
    } catch (e) {
      setErr(e?.message || "Failed to delete");
    } finally {
      setBusy(false);
    }
  };

  const afterSaveRef = useRef(null);
  const openEditProduct = (p, afterSave = null) => {
    afterSaveRef.current = afterSave;
    setEditingProduct(p);
    setShowAddProduct(true);
  };

  // ── Excel round-trip for the Catalog sub-panel ──────────────────
  const handleLoadCatalogToExcel = async () => {
    setBusy(true);
    try {
      // Fetch ALL classifications + products for the current kind (flat)
      const [clList, prodList] = await Promise.all([
        fetchClassifications(token, { kind, parentCode: "__all__" }),
        fetchProducts(token, { kind, limit: 5000 }),
      ]);
      const clSheet = await writeClassificationsToExcel(kind, clList || []);
      const pSheet = await writeProductsToExcel(kind, prodList || []);
      setStatus(`Loaded ${(clList || []).length} classifications + ${(prodList || []).length} products to "${clSheet}" / "${pSheet}". Edit, then "Save from Excel".`);
    } catch (e) {
      setErr(e?.message || "Failed to load to Excel");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveCatalogFromExcel = async () => {
    if (!canEdit) return setErr("You have no edit access here.");
    setBusy(true);
    try {
      // Read both sheets if present; track whether each sheet exists at all.
      let clRows = [], prodRows = [];
      let prodSheetExists = false;
      try { clRows = await readClassificationsFromExcel(kind); } catch { clRows = []; }
      try { prodRows = await readProductsFromExcel(kind); prodSheetExists = true; } catch { prodRows = []; }
      if (!clRows.length && !prodSheetExists) {
        return setErr(`Neither "Classifications_${kind}" nor "Products_${kind}" sheet found. Click "Load to Excel" first.`);
      }

      const summary = [];
      let totalErrors = 0;
      const errorSamples = [];

      if (clRows.length) {
        const r = await bulkClassifications(token, clRows);
        summary.push(`classifications: +${r.written.created}/~${r.written.updated}${r.error_count ? ` (${r.error_count} err)` : ""}`);
        totalErrors += r.error_count || 0;
        (r.errors || []).forEach((e) => errorSamples.push(`cls row ${e.row}: ${e.error}`));
      }
      // Chunked bulk save for products.
      // Strategy: upload all chunks without any server-side delete, then send
      // an explicit ids_to_delete list (products in DB before upload that are
      // no longer in the sheet). This avoids last-chunk overwriting earlier chunks.
      if (prodSheetExists) {
        // IDs of products already in DB before this upload
        const preUpload = await fetchProducts(token, { kind, limit: 99999 });
        const preUploadIds = new Set((preUpload || []).map((p) => p.id));
        // IDs the Excel sheet knows about (existing rows only; new rows have no ID)
        const excelIds = new Set(
          prodRows.map((r) => Number(r.ID || r.id)).filter(Boolean)
        );
        // Products in DB but removed from the sheet → delete after upload
        const idsToDelete = [...preUploadIds].filter((id) => !excelIds.has(id));

        const CHUNK = 400;
        const agg = { created: 0, updated: 0, deleted: 0 };
        const chunks = [];
        for (let i = 0; i < prodRows.length; i += CHUNK) chunks.push(prodRows.slice(i, i + CHUNK));
        if (chunks.length === 0) chunks.push([]); // still need the delete pass

        for (let ci = 0; ci < chunks.length; ci++) {
          setStatus(`Saving products… ${ci + 1}/${chunks.length}`);
          const isLast = ci === chunks.length - 1;
          const r = await bulkProducts(token, chunks[ci], {
            kind,
            deleteMissing: false,          // never auto-delete mid-chunk
            idsToDelete: isLast ? idsToDelete : null,  // explicit delete only on last pass
          });
          agg.created += r.written.created || 0;
          agg.updated += r.written.updated || 0;
          agg.deleted += r.written.deleted || 0;
          totalErrors += r.error_count || 0;
          (r.errors || []).forEach((e) => errorSamples.push(`prod row ${e.row}: ${e.error}`));
        }
        summary.push(`products: +${agg.created}/~${agg.updated}/-${agg.deleted}${totalErrors ? ` (${totalErrors} err)` : ""}`);
      }

      setStatus(`Saved — ${summary.join(", ")}.`);
      if (totalErrors > 0) {
        setErr(`${totalErrors} row(s) failed: ${errorSamples.slice(0, 3).join("; ")}${totalErrors > 3 ? " …" : ""}`);
      }
      reloadTree();
    } catch (e) {
      setErr(e?.message || "Failed to save from Excel");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {/* Kind picker */}
      <div className="sheet-lookup-chip-row" style={{ marginBottom: 6 }}>
        {KINDS.map((k) => {
          const isActive = kind === k.id;
          return (
            <button
              key={k.id}
              onClick={() => { setKind(k.id); }}
              className="sheet-lookup-chip"
              style={isActive ? {
                borderColor: k.color,
                borderWidth: 2,
                background: k.color + "33",
                color: "#fff",
                fontWeight: 700,
                boxShadow: `0 0 8px ${k.color}66`,
              } : {
                borderColor: k.color + "88",
                color: "#94a3b8",
              }}
            >
              <span style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: k.color,
                marginRight: 5,
                opacity: isActive ? 1 : 0.4,
                verticalAlign: "middle",
              }} />
              {k.label}
            </button>
          );
        })}
      </div>

      {/* Excel bulk edit */}
      <div className="panel" style={{ marginBottom: 8, padding: 8 }}>
        <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>
          Excel bulk edit — load all {kind.toLowerCase()} classifications + products into 2 sheets,
          edit them in Excel, then save back.
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className="boq-action-btn" onClick={handleLoadCatalogToExcel} disabled={busy}>
            ↧ Load to Excel
          </button>
          {canEdit && (
            <button className="primary" onClick={handleSaveCatalogFromExcel} disabled={busy}>
              ✓ Save from Excel
            </button>
          )}
        </div>
      </div>

      {/* Search + add-root */}
      <div className="panel" style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <input
            placeholder="search code / name (whole catalog)..."
            value={searchClass}
            onChange={(e) => setSearchClass(e.target.value)}
            style={{ flex: "1 1 160px" }}
          />
          {canEdit && (
            <button className="boq-action-btn" onClick={() => setShowAddRoot((v) => !v)} disabled={busy}>
              {showAddRoot ? "✕ Cancel" : "+ Add root category"}
            </button>
          )}
        </div>

        {showAddRoot && (
          <div style={{ display: "grid", gap: 4, marginTop: 8, padding: 8, border: "1px solid #334155", borderRadius: 4 }}>
            <RequiredLegend />
            <Row label="Code" required hint="e.g. 01.00.00.00.00">
              <input value={newClass.code} onChange={(e) => setNewClass({ ...newClass, code: e.target.value })} />
            </Row>
            <Row label="Name GEO" required>
              <input value={newClass.name_geo} onChange={(e) => setNewClass({ ...newClass, name_geo: e.target.value })} />
            </Row>
            <Row label="Name ENG">
              <input value={newClass.name_eng} onChange={(e) => setNewClass({ ...newClass, name_eng: e.target.value })} />
            </Row>
            <Row label="Name RUS">
              <input value={newClass.name_rus} onChange={(e) => setNewClass({ ...newClass, name_rus: e.target.value })} />
            </Row>
            <button className="primary" onClick={handleAddRoot}
              disabled={busy || !newClass.code.trim() || !newClass.name_geo.trim()}>
              Save root category (level 1)
            </button>
          </div>
        )}
      </div>

      {/* Folder/file tree (scrollable) */}
      <div className="panel">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <strong style={{ fontSize: 12 }}>
            {searchResults !== null ? `Search results (${searchResults.length})` : "კატალოგი / Catalog tree"}
          </strong>
          {searchResults === null && (
            <button
              title="Refresh tree"
              onClick={() => { setTreeKey(k => k + 1); setRoots([]); }}
              style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, padding: "0 2px", lineHeight: 1 }}
            >
              ↺
            </button>
          )}
        </div>
        <div style={{ maxHeight: 460, overflow: "auto", marginTop: 6, border: "1px solid #1e293b", borderRadius: 4 }}>
          {searchResults !== null ? (
            // Flat search results — click to jump (shown as standalone folder rows)
            searchResults.length === 0 ? (
              <div style={{ color: "#94a3b8", fontSize: 11, padding: 8 }}>No matches.</div>
            ) : (
              searchResults.map((cl) => (
                <TreeNode
                  key={cl.code}
                  token={token} kind={kind} node={cl} depth={0}
                  canEdit={canEdit} busy={busy} setBusy={setBusy}
                  setStatus={setStatus} setErr={setErr}
                  onEditProduct={openEditProduct} onDeleteClass={handleDeleteClass}
                  onTreeChanged={reloadTree}
                />
              ))
            )
          ) : roots.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 11, padding: 8 }}>
              No categories yet. {canEdit && "Click \"+ Add root category\" to create one."}
            </div>
          ) : (
            roots.map((cl) => (
              <TreeNode
                key={cl.code}
                token={token} kind={kind} node={cl} depth={0}
                canEdit={canEdit} busy={busy} setBusy={setBusy}
                setStatus={setStatus} setErr={setErr}
                onEditProduct={openEditProduct} onDeleteClass={handleDeleteClass}
                onTreeChanged={reloadTree}
              />
            ))
          )}
        </div>
      </div>

      {showAddProduct && editingProduct && (
        <ProductEditModal
          token={token}
          product={editingProduct}
          onClose={() => { setShowAddProduct(false); setEditingProduct(null); }}
          onSaved={() => {
            const cb = afterSaveRef.current;
            afterSaveRef.current = null;
            setShowAddProduct(false);
            setEditingProduct(null);
            if (cb) cb();
            else reloadTree();
          }}
          setErr={setErr}
        />
      )}
    </div>
  );
}

// ── Recursive tree node: a classification "folder" that lazily loads its
//    sub-classifications + products when expanded. Products render as leaves. ──

function TreeNode({
  token, kind, node, depth, canEdit, busy, setBusy, setStatus, setErr,
  onEditProduct, onDeleteClass, onTreeChanged,
}) {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState(null);   // sub-classifications
  const [products, setProducts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddChild, setShowAddChild] = useState(false);
  const [newChild, setNewChild] = useState({ code: "", name_geo: "", name_eng: "", name_rus: "" });
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState({ code: node.code, name_geo: node.name_geo || "", name_eng: node.name_eng || "", name_rus: node.name_rus || "" });

  const name = node.name_geo || node.name_eng || node.name_rus || node.code;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cls, prods] = await Promise.all([
        fetchClassifications(token, { kind, parentCode: node.code }),
        fetchProducts(token, { kind, classificationCode: node.code }),
      ]);
      setChildren(cls || []);
      setProducts(prods || []);
    } catch (e) {
      setErr(e?.message || "Failed to load node");
    } finally {
      setLoading(false);
    }
  }, [token, kind, node.code, setErr]);

  const toggle = async () => {
    if (!expanded && children === null) await load();
    setExpanded(!expanded);
  };

  const reloadSelf = () => { load(); };

  const handleAddChild = async () => {
    if (!newChild.code.trim()) return setErr("Code required");
    setBusy(true);
    try {
      await upsertClassification(token, {
        code: newChild.code.trim(),
        kind,
        level: (node.level || 1) + 1,
        parent_code: node.code,
        name_geo: newChild.name_geo || null,
        name_eng: newChild.name_eng || null,
        name_rus: newChild.name_rus || null,
      });
      setShowAddChild(false);
      setNewChild({ code: "", name_geo: "", name_eng: "", name_rus: "" });
      setStatus(`Sub-category ${newChild.code} added under ${node.code}.`);
      if (!expanded) setExpanded(true);
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to add sub-category");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editData.code.trim()) return setErr("Code required");
    setBusy(true);
    try {
      await upsertClassification(token, {
        code: editData.code.trim(),
        kind,
        level: node.level,
        parent_code: node.parent_code || null,
        name_geo: editData.name_geo || null,
        name_eng: editData.name_eng || null,
        name_rus: editData.name_rus || null,
      });
      setShowEdit(false);
      setStatus(`Category ${editData.code} updated.`);
      onTreeChanged();
    } catch (e) {
      setErr(e?.message || "Failed to update category");
    } finally {
      setBusy(false);
    }
  };

  const indent = depth * 14;
  const childCount = children === null ? null : children.length;
  const prodCount = products === null ? null : products.length;

  return (
    <div>
      {/* Folder row */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: 4,
          padding: "3px 4px", paddingLeft: indent + 4,
          borderBottom: "1px solid #1e293b",
          background: expanded ? "rgba(20,184,166,0.06)" : "transparent",
        }}
      >
        <button onClick={toggle}
          style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 11, width: 14, padding: 0 }}>
          {expanded ? "▾" : "▸"}
        </button>
        <span style={{ fontSize: 13 }}>📁</span>
        <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={toggle}>
          <span style={{ fontSize: 11, color: "#67e8f9", fontFamily: "monospace" }}>{node.code}</span>
          <span style={{ fontSize: 12, marginLeft: 6 }}>{name}</span>
        </div>
        <span style={{ fontSize: 9, color: "#64748b", marginRight: 4 }}>L{node.level}</span>
        {canEdit && (
          <>
            <button className="boq-action-btn" style={{ padding: "1px 5px", fontSize: 10 }}
              title="edit category"
              onClick={(e) => { e.stopPropagation(); setEditData({ code: node.code, name_geo: node.name_geo || "", name_eng: node.name_eng || "", name_rus: node.name_rus || "" }); setShowEdit((v) => !v); }}
              disabled={busy}>✏</button>
            <button className="boq-action-btn" style={{ padding: "1px 5px", fontSize: 10 }}
              title="add sub-category"
              onClick={(e) => { e.stopPropagation(); setShowAddChild((v) => !v); if (!expanded) toggle(); }}
              disabled={busy}>+📁</button>
            <button className="boq-action-btn" style={{ padding: "1px 5px", fontSize: 10 }}
              title="add product here"
              onClick={(e) => {
                e.stopPropagation();
                if (!expanded) { setExpanded(true); if (children === null) load(); }
                onEditProduct(
                  { kind, classification_code: node.code, is_generic: false },
                  () => { load(); setExpanded(true); }
                );
              }}
              disabled={busy}>+📄</button>
            <button className="boq-action-btn" style={{ padding: "1px 5px", fontSize: 10 }}
              title="delete category"
              onClick={(e) => { e.stopPropagation(); onDeleteClass(node); }}
              disabled={busy}>✕</button>
          </>
        )}
      </div>

      {/* Inline edit form */}
      {showEdit && (
        <div style={{ paddingLeft: indent + 24, paddingRight: 6, paddingBottom: 6, paddingTop: 4, display: "grid", gap: 3, background: "rgba(6,182,212,0.05)", borderBottom: "1px solid #1e293b" }}>
          <input placeholder="Code *" value={editData.code}
            onChange={(e) => setEditData({ ...editData, code: e.target.value })} style={{ fontSize: 11 }} />
          <input placeholder="Name GEO *" value={editData.name_geo}
            onChange={(e) => setEditData({ ...editData, name_geo: e.target.value })} style={{ fontSize: 11 }} />
          <div style={{ display: "flex", gap: 4 }}>
            <input placeholder="Name ENG" value={editData.name_eng}
              onChange={(e) => setEditData({ ...editData, name_eng: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <input placeholder="Name RUS" value={editData.name_rus}
              onChange={(e) => setEditData({ ...editData, name_rus: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            <button className="primary" style={{ fontSize: 11, flex: 1 }} onClick={handleSaveEdit}
              disabled={busy || !editData.code.trim() || !editData.name_geo.trim()}>
              Save
            </button>
            <button className="boq-action-btn" style={{ fontSize: 11 }} onClick={() => setShowEdit(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Inline add-child form */}
      {showAddChild && (
        <div style={{ paddingLeft: indent + 24, paddingRight: 6, paddingBottom: 6, display: "grid", gap: 3 }}>
          <input placeholder="Code (e.g. 01.07.00.00.00)" value={newChild.code}
            onChange={(e) => setNewChild({ ...newChild, code: e.target.value })} style={{ fontSize: 11 }} />
          <input placeholder="Name GEO *" value={newChild.name_geo}
            onChange={(e) => setNewChild({ ...newChild, name_geo: e.target.value })} style={{ fontSize: 11 }} />
          <div style={{ display: "flex", gap: 4 }}>
            <input placeholder="Name ENG" value={newChild.name_eng}
              onChange={(e) => setNewChild({ ...newChild, name_eng: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <input placeholder="Name RUS" value={newChild.name_rus}
              onChange={(e) => setNewChild({ ...newChild, name_rus: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
          </div>
          <button className="primary" style={{ fontSize: 11 }} onClick={handleAddChild}
            disabled={busy || !newChild.code.trim() || !newChild.name_geo.trim()}>
            Save sub-category (level {(node.level || 1) + 1})
          </button>
        </div>
      )}

      {/* Expanded contents: children folders, then product leaves */}
      {expanded && (
        <div>
          {loading && (
            <div style={{ paddingLeft: indent + 24, fontSize: 11, color: "#94a3b8", padding: 4 }}>Loading…</div>
          )}
          {!loading && childCount === 0 && prodCount === 0 && (
            <div style={{ paddingLeft: indent + 24, fontSize: 11, color: "#64748b", padding: 4 }}>
              empty {canEdit && "— use +📁 / +📄 to add"}
            </div>
          )}
          {(children || []).map((c) => (
            <TreeNode
              key={c.code}
              token={token} kind={kind} node={c} depth={depth + 1}
              canEdit={canEdit} busy={busy} setBusy={setBusy}
              setStatus={setStatus} setErr={setErr}
              onEditProduct={onEditProduct} onDeleteClass={onDeleteClass}
              onTreeChanged={onTreeChanged}
            />
          ))}
          {(products || []).map((p) => (
            <div key={p.id} style={{ paddingLeft: indent + 18, borderBottom: "1px solid #131c2e" }}>
              <ProductRow
                product={p}
                token={token}
                canEdit={canEdit}
                onChange={reloadSelf}
                setStatus={setStatus}
                setErr={setErr}
                busy={busy}
                setBusy={setBusy}
                onEdit={() => onEditProduct(p, reloadSelf)}
                leafIcon
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── A single product row with expandable supplier-offers ───────────

function ProductRow({ product, token, canEdit, onChange, setStatus, setErr, busy, setBusy, onEdit, leafIcon = false }) {
  const [open, setOpen] = useState(false);
  const [offers, setOffers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [newOffer, setNewOffer] = useState({ supplier_id: "", unit_rate: "", currency: "GEL" });

  const loadOffers = useCallback(async () => {
    try {
      const list = await fetchOffers(token, { productId: product.id });
      setOffers(list || []);
    } catch (e) {
      setErr(e?.message || "Failed to load offers");
    }
  }, [token, product.id, setErr]);

  const loadSuppliers = useCallback(async () => {
    try {
      const list = await fetchSuppliers(token, { isActive: true });
      setSuppliers(list || []);
    } catch {
      setSuppliers([]);
    }
  }, [token]);

  const toggle = () => {
    if (!open) { loadOffers(); loadSuppliers(); }
    setOpen(!open);
  };

  const handleAddOffer = async () => {
    if (!newOffer.supplier_id || !newOffer.unit_rate) return setErr("Supplier and rate required");
    setBusy(true);
    try {
      await createOffer(token, {
        supplier_id: Number(newOffer.supplier_id),
        product_id: product.id,
        unit_rate: Number(newOffer.unit_rate),
        currency: newOffer.currency || "GEL",
      });
      setNewOffer({ supplier_id: "", unit_rate: "", currency: "GEL" });
      loadOffers();
      setStatus("Offer added.");
    } catch (e) {
      setErr(e?.message || "Failed to add offer");
    } finally {
      setBusy(false);
    }
  };

  const handleSetPreferred = async (offer) => {
    if (offer.is_preferred) return;
    setBusy(true);
    try {
      await updateOffer(token, offer.id, { is_preferred: true });
      loadOffers();
    } catch (e) {
      setErr(e?.message || "Failed to set preferred");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteOffer = async (id) => {
    if (!(await confirmDialog("Delete this offer?"))) return;
    setBusy(true);
    try {
      await deleteOffer(token, id);
      loadOffers();
    } catch (e) {
      setErr(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!(await confirmDialog(`Delete product "${product.display_id || product.name_eng || product.id}"?`))) return;
    setBusy(true);
    try {
      await deleteProduct(token, product.id);
      onChange();
    } catch (e) {
      setErr(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const minPrice = offers.length > 0 ? Math.min(...offers.map((o) => o.unit_rate)) : null;

  return (
    <div style={{ borderBottom: "1px solid #1e293b", padding: "4px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button onClick={toggle} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: 0, fontSize: 12 }}>
          {open ? "▾" : "▸"}
        </button>
        {leafIcon && <span style={{ fontSize: 12 }}>📄</span>}
        <div style={{ flex: 1, cursor: "pointer" }} onClick={toggle}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace" }}>
            {product.display_id || `#${product.id}`} {product.is_generic && <span style={{ color: "#fbbf24" }}>· generic</span>}
          </div>
          <div style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {product.name_geo || product.name_eng || product.name_rus}
          </div>
          <div style={{ fontSize: 10, color: "#94a3b8" }}>
            {product.base_unit} {product.brand_name ? ` · ${product.brand_name}` : ""}
            {minPrice !== null && ` · from ${minPrice} GEL`}
          </div>
        </div>
        {canEdit && (
          <>
            <button className="boq-action-btn" style={{ padding: "2px 6px", fontSize: 11 }} onClick={onEdit} disabled={busy}>edit</button>
            <button className="boq-action-btn" style={{ padding: "2px 6px", fontSize: 11 }} onClick={handleDeleteProduct} disabled={busy}>✕</button>
          </>
        )}
      </div>

      {open && (
        <div style={{ marginLeft: 18, marginTop: 4, padding: 6, background: "rgba(15,23,42,0.4)", borderRadius: 4 }}>
          <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4 }}>Supplier offers ({offers.length}):</div>
          {offers.length === 0 && <div style={{ fontSize: 11, color: "#64748b" }}>No prices yet.</div>}
          {offers.map((o) => (
            <div key={o.id} style={{ display: "flex", fontSize: 11, padding: "2px 0", borderTop: "1px dotted #334155" }}>
              <span style={{ flex: 1 }}>
                {canEdit
                  ? <button
                      onClick={() => handleSetPreferred(o)}
                      disabled={busy || o.is_preferred}
                      title={o.is_preferred ? "Default price" : "Set as default price"}
                      style={{ background: "none", border: "none", cursor: o.is_preferred ? "default" : "pointer", padding: "0 4px 0 0", color: o.is_preferred ? "#34d399" : "#475569", fontSize: 12 }}
                    >★</button>
                  : o.is_preferred && <span style={{ color: "#34d399", marginRight: 2 }}>★</span>
                }
                {o.supplier_name}
              </span>
              <span style={{ width: 90, textAlign: "right" }}>{o.unit_rate} {o.currency}</span>
              {o.valid_to && <span style={{ width: 90, textAlign: "right", color: "#fbbf24" }}>til {o.valid_to}</span>}
              {canEdit && (
                <button className="boq-action-btn" style={{ padding: "1px 4px", fontSize: 10, marginLeft: 4 }}
                  onClick={() => handleDeleteOffer(o.id)} disabled={busy}>✕</button>
              )}
            </div>
          ))}
          {canEdit && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 70px 50px auto", gap: 4, marginTop: 6, alignItems: "center" }}>
              <SearchableSelect
                options={suppliers.map((s) => ({ value: String(s.id), label: s.name }))}
                value={String(newOffer.supplier_id)}
                onChange={(v) => setNewOffer({ ...newOffer, supplier_id: v })}
                placeholder="search supplier…"
              />
              <input type="number" placeholder="rate" value={newOffer.unit_rate}
                onChange={(e) => setNewOffer({ ...newOffer, unit_rate: e.target.value })}
                style={{ fontSize: 11 }} />
              <input value={newOffer.currency}
                onChange={(e) => setNewOffer({ ...newOffer, currency: e.target.value })}
                style={{ fontSize: 11 }} />
              <button className="primary" style={{ padding: "2px 8px", fontSize: 11 }} onClick={handleAddOffer} disabled={busy}>+ offer</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Product create/edit modal (inline, no real modal) ─────────────

function SectionHeader({ label, open, onToggle }) {
  return (
    <div
      onClick={onToggle}
      style={{ cursor: "pointer", fontSize: 11, fontWeight: 700, color: "#67e8f9",
        borderBottom: "1px solid #1e293b", paddingBottom: 3, marginTop: 10, marginBottom: 4,
        display: "flex", justifyContent: "space-between" }}
    >
      {label}
      <span style={{ color: "#64748b" }}>{open ? "▴" : "▾"}</span>
    </div>
  );
}

function ProductEditModal({ token, product, onClose, onSaved, setErr }) {
  const [savedId, setSavedId] = useState(product.id || null);
  const isNew = !savedId;
  const [data, setData] = useState({
    kind:                    product.kind || "MATERIAL",
    classification_code:     product.classification_code || "",
    display_id:              product.display_id || "",
    is_generic:              product.is_generic || false,
    status:                  product.status || "",
    // Names
    name_geo:                product.name_geo || "",
    name_eng:                product.name_eng || "",
    name_rus:                product.name_rus || "",
    // Descriptions
    description_geo:         product.description_geo || "",
    description_eng:         product.description_eng || "",
    description_rus:         product.description_rus || "",
    // Units
    base_unit:               product.base_unit || "",
    base_unit_value:         product.base_unit_value ?? 1,
    secondary_unit:          product.secondary_unit || "",
    secondary_unit_factor:   product.secondary_unit_factor ?? "",
    purchase_unit:           product.purchase_unit || "",
    purchase_unit_factor:    product.purchase_unit_factor ?? "",
    // Physical
    density_unit:            product.density_unit || "",
    density:                 product.density ?? "",
    length:                  product.length ?? "",
    width:                   product.width ?? "",
    height:                  product.height ?? "",
    thickness:               product.thickness ?? "",
    diameter:                product.diameter ?? "",
    weight_per_unit:         product.weight_per_unit ?? "",
    // Product info
    discipline:              product.discipline || "",
    brand_name:              product.brand_name || "",
    product_model:           product.product_model || "",
    manufacturer:            product.manufacturer || "",
    manufacturing_location:  product.manufacturing_location || "",
    // Standard codes
    gesn_code:               product.gesn_code || "",
    costy_code:              product.costy_code || "",
    etim_code:               product.etim_code || "",
    uniformat_code:          product.uniformat_code || "",
    omniclass_code:          product.omniclass_code || "",
    masterformat_code:       product.masterformat_code || "",
  });
  const [saving, setSaving] = useState(false);
  const [units, setUnits] = useState([]);
  const [openSections, setOpenSections] = useState({ names: true, units: true, descriptions: false, physical: false, product: true, codes: false });

  useEffect(() => {
    fetchUnits(token).then((list) => setUnits(list || [])).catch(() => setUnits([]));
  }, [token]);

  const unitOptions = useMemo(() => (units || []).map((u) => ({
    value: u.code,
    label: `${u.code}  —  ${u.symbol || ""} (${u.dimension})${u.is_base ? " · base" : ""}`,
  })), [units]);

  const toggleSection = (key) => setOpenSections((s) => ({ ...s, [key]: !s[key] }));
  const f = (field) => (e) => setData((d) => ({ ...d, [field]: e.target.value }));
  const fNum = (field) => (e) => setData((d) => ({ ...d, [field]: e.target.value === "" ? "" : Number(e.target.value) }));

  const canSave = useMemo(() => (
    Boolean(data.classification_code?.trim()) &&
    Boolean(data.display_id?.trim()) &&
    Boolean(data.name_geo?.trim()) &&
    Boolean(data.base_unit?.trim()) &&
    data.base_unit_value !== "" && data.base_unit_value !== null && data.base_unit_value !== undefined
  ), [data]);

  const save = async () => {
    setSaving(true);
    try {
      const FLOAT_FIELDS = [
        "secondary_unit_factor", "purchase_unit_factor",
        "density", "length", "width", "height", "thickness", "diameter", "weight_per_unit",
      ];
      const UNIT_FK_FIELDS = ["secondary_unit", "purchase_unit", "density_unit", "geometry_unit"];
      const payload = { ...data };
      FLOAT_FIELDS.forEach((key) => {
        if (payload[key] === "" || payload[key] === undefined) payload[key] = null;
      });
      UNIT_FK_FIELDS.forEach((key) => {
        if (payload[key] === "" || payload[key] === undefined) payload[key] = null;
      });
      if (isNew) {
        const created = await createProduct(token, payload);
        setSavedId(created.id);
      } else {
        await updateProduct(token, savedId, payload);
      }
      onSaved();
    } catch (e) {
      setErr(e?.message || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: "#0f172a", border: "1px solid #334155", borderRadius: 6,
        padding: 16, width: "100%", maxWidth: 620, maxHeight: "90vh", overflow: "auto",
      }}>
        <h3 style={{ margin: "0 0 6px", fontSize: 14 }}>
          {isNew ? "New product" : `Edit product #${savedId}`}
        </h3>
        <RequiredLegend />

        {/* ── Identity ── */}
        <div style={{ display: "grid", gap: 5, marginTop: 6 }}>
          <Row label="Classification" required hint="parent folder code">
            <input value={data.classification_code} onChange={f("classification_code")} />
          </Row>
          <Row label="Display ID" required hint="e.g. 01.07.03.01-0001">
            <input value={data.display_id} onChange={f("display_id")} />
          </Row>
          <Row label="Generic?" hint="placeholder — user picks specific product at budget time">
            <input type="checkbox" checked={data.is_generic} onChange={(e) => setData((d) => ({ ...d, is_generic: e.target.checked }))} />
          </Row>
          <Row label="Status" hint="active / inactive / draft">
            <input value={data.status} onChange={f("status")} placeholder="active" />
          </Row>
        </div>

        {/* ── Names ── */}
        <SectionHeader label="Names" open={openSections.names} onToggle={() => toggleSection("names")} />
        {openSections.names && (
          <div style={{ display: "grid", gap: 5 }}>
            <Row label="Name GEO" required><input value={data.name_geo} onChange={f("name_geo")} /></Row>
            <Row label="Name ENG"><input value={data.name_eng} onChange={f("name_eng")} /></Row>
            <Row label="Name RUS"><input value={data.name_rus} onChange={f("name_rus")} /></Row>
          </div>
        )}

        {/* ── Descriptions ── */}
        <SectionHeader label="Descriptions" open={openSections.descriptions} onToggle={() => toggleSection("descriptions")} />
        {openSections.descriptions && (
          <div style={{ display: "grid", gap: 5 }}>
            <Row label="Description GEO"><input value={data.description_geo} onChange={f("description_geo")} /></Row>
            <Row label="Description ENG"><input value={data.description_eng} onChange={f("description_eng")} /></Row>
            <Row label="Description RUS"><input value={data.description_rus} onChange={f("description_rus")} /></Row>
          </div>
        )}

        {/* ── Units ── */}
        <SectionHeader label="Units" open={openSections.units} onToggle={() => toggleSection("units")} />
        {openSections.units && (
          <div style={{ display: "grid", gap: 5 }}>
            <Row label="Base unit" required hint="primary unit for budgeting math">
              <SearchableSelect options={unitOptions} value={data.base_unit}
                onChange={(v) => setData((d) => ({ ...d, base_unit: v }))}
                placeholder="search unit (m, kg, m3, hr…)" />
            </Row>
            <Row label="Base unit value" required hint="e.g. 1 or 100">
              <input type="number" value={data.base_unit_value}
                onChange={(e) => setData((d) => ({ ...d, base_unit_value: e.target.value === "" ? 1 : Number(e.target.value) }))} />
            </Row>
            <Row label="Secondary unit" hint="alternate measurement unit">
              <SearchableSelect options={unitOptions} value={data.secondary_unit}
                onChange={(v) => setData((d) => ({ ...d, secondary_unit: v }))}
                placeholder="optional" />
            </Row>
            <Row label="Secondary factor" hint="1 secondary = N base units">
              <input type="number" value={data.secondary_unit_factor} onChange={fNum("secondary_unit_factor")} placeholder="e.g. 1000" />
            </Row>
            <Row label="Purchase unit" hint="unit used when ordering from supplier">
              <SearchableSelect options={unitOptions} value={data.purchase_unit}
                onChange={(v) => setData((d) => ({ ...d, purchase_unit: v }))}
                placeholder="optional" />
            </Row>
            <Row label="Purchase factor" hint="1 purchase unit = N base units">
              <input type="number" value={data.purchase_unit_factor} onChange={fNum("purchase_unit_factor")} placeholder="e.g. 25" />
            </Row>
          </div>
        )}

        {/* ── Physical dimensions ── */}
        <SectionHeader label="Physical dimensions" open={openSections.physical} onToggle={() => toggleSection("physical")} />
        {openSections.physical && (
          <div style={{ display: "grid", gap: 5 }}>
            <Row label="Density unit"><input value={data.density_unit} onChange={f("density_unit")} placeholder="e.g. kg/m3" /></Row>
            <Row label="Density"><input type="number" value={data.density} onChange={fNum("density")} /></Row>
            <Row label="Length (m)"><input type="number" value={data.length} onChange={fNum("length")} /></Row>
            <Row label="Width (m)"><input type="number" value={data.width} onChange={fNum("width")} /></Row>
            <Row label="Height (m)"><input type="number" value={data.height} onChange={fNum("height")} /></Row>
            <Row label="Thickness (m)"><input type="number" value={data.thickness} onChange={fNum("thickness")} /></Row>
            <Row label="Diameter (m)"><input type="number" value={data.diameter} onChange={fNum("diameter")} /></Row>
            <Row label="Weight / unit"><input type="number" value={data.weight_per_unit} onChange={fNum("weight_per_unit")} /></Row>
          </div>
        )}

        {/* ── Product info ── */}
        <SectionHeader label="Product info" open={openSections.product} onToggle={() => toggleSection("product")} />
        {openSections.product && (
          <div style={{ display: "grid", gap: 5 }}>
            <Row label="Discipline"><input value={data.discipline} onChange={f("discipline")} placeholder="e.g. Civil, MEP" /></Row>
            <Row label="Brand"><input value={data.brand_name} onChange={f("brand_name")} /></Row>
            <Row label="Model"><input value={data.product_model} onChange={f("product_model")} /></Row>
            <Row label="Manufacturer"><input value={data.manufacturer} onChange={f("manufacturer")} /></Row>
            <Row label="Manuf. location"><input value={data.manufacturing_location} onChange={f("manufacturing_location")} /></Row>
          </div>
        )}

        {/* ── Standard codes ── */}
        <SectionHeader label="Standard codes" open={openSections.codes} onToggle={() => toggleSection("codes")} />
        {openSections.codes && (
          <div style={{ display: "grid", gap: 5 }}>
            <Row label="GESN code"><input value={data.gesn_code} onChange={f("gesn_code")} /></Row>
            <Row label="Costy code"><input value={data.costy_code} onChange={f("costy_code")} /></Row>
            <Row label="ETIM code"><input value={data.etim_code} onChange={f("etim_code")} /></Row>
            <Row label="UniFormat code"><input value={data.uniformat_code} onChange={f("uniformat_code")} /></Row>
            <Row label="OmniClass code"><input value={data.omniclass_code} onChange={f("omniclass_code")} /></Row>
            <Row label="MasterFormat code"><input value={data.masterformat_code} onChange={f("masterformat_code")} /></Row>
          </div>
        )}

        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 14 }}>
          <button className="boq-action-btn" onClick={onClose} disabled={saving}>Close</button>
          <button className="primary" onClick={save} disabled={saving || !canSave}>
            {saving ? "…" : isNew ? "Save" : "Save changes"}
          </button>
        </div>

        {savedId && <ProductOffersInline token={token} productId={savedId} setErr={setErr} />}
        {!savedId && (
          <div style={{ marginTop: 14, padding: 10, border: "1px dashed #334155", borderRadius: 4, fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
            Save the product first, then add supplier prices below.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Inline supplier-offers editor (shown inside the product modal) ─

function ProductOffersInline({ token, productId, setErr }) {
  const [offers, setOffers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [newOffer, setNewOffer] = useState({ supplier_id: "", unit_rate: "", currency: "GEL", valid_from: "", valid_to: "" });
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [o, s] = await Promise.all([
        fetchOffers(token, { productId }),
        fetchSuppliers(token, { isActive: true }),
      ]);
      setOffers(o || []);
      setSuppliers(s || []);
    } catch (e) {
      setErr(e?.message || "Failed to load offers");
    }
  }, [token, productId, setErr]);

  useEffect(() => { reload(); }, [reload]);

  const supplierOptions = useMemo(
    () => (suppliers || []).map((s) => ({ value: s.id, label: s.name })),
    [suppliers]
  );

  const handleAdd = async () => {
    if (!newOffer.supplier_id) return setErr("Pick a supplier first.");
    if (!newOffer.unit_rate) return setErr("Enter the unit rate.");
    setBusy(true);
    try {
      const payload = {
        supplier_id: Number(newOffer.supplier_id),
        product_id: productId,
        unit_rate: Number(newOffer.unit_rate),
        currency: newOffer.currency || "GEL",
      };
      if (newOffer.valid_from) payload.valid_from = newOffer.valid_from;
      if (newOffer.valid_to) payload.valid_to = newOffer.valid_to;
      await createOffer(token, payload);
      setNewOffer({ supplier_id: "", unit_rate: "", currency: "GEL", valid_from: "", valid_to: "" });
      reload();
    } catch (e) {
      setErr(e?.message || "Failed to add offer");
    } finally {
      setBusy(false);
    }
  };

  const handleSetPreferred = async (offer) => {
    if (offer.is_preferred) return;
    setBusy(true);
    try {
      await updateOffer(token, offer.id, { is_preferred: true });
      reload();
    } catch (e) {
      setErr(e?.message || "Failed to set preferred");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog("Delete this offer?"))) return;
    setBusy(true);
    try {
      await deleteOffer(token, id);
      reload();
    } catch (e) {
      setErr(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      marginTop: 14, padding: 10, border: "1px solid #334155", borderRadius: 4,
      background: "rgba(15,23,42,0.4)",
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
        Supplier offers ({offers.length})
      </div>

      {offers.length === 0 && (
        <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8 }}>
          No supplier prices yet. Add one below.
        </div>
      )}
      {offers.map((o) => (
        <div key={o.id} style={{
          display: "flex", gap: 6, padding: "4px 0",
          borderTop: "1px dotted #334155", fontSize: 11,
        }}>
          <span style={{ flex: 1 }}>
            <button
              onClick={() => handleSetPreferred(o)}
              disabled={busy || o.is_preferred}
              title={o.is_preferred ? "Default price" : "Set as default price"}
              style={{ background: "none", border: "none", cursor: o.is_preferred ? "default" : "pointer", padding: "0 4px 0 0", color: o.is_preferred ? "#34d399" : "#475569", fontSize: 12 }}
            >★</button>
            {o.supplier_name}
          </span>
          <span style={{ width: 100, textAlign: "right" }}>{o.unit_rate} {o.currency}</span>
          {o.valid_from && <span style={{ width: 90, color: "#94a3b8" }}>from {o.valid_from}</span>}
          {o.valid_to && <span style={{ width: 90, color: "#fbbf24" }}>til {o.valid_to}</span>}
          <button className="boq-action-btn" style={{ padding: "1px 6px", fontSize: 10 }}
            onClick={() => handleDelete(o.id)} disabled={busy}>✕</button>
        </div>
      ))}

      {/* Add new offer */}
      <div style={{ marginTop: 10, padding: 8, border: "1px dashed #334155", borderRadius: 4 }}>
        <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4 }}>+ add new price offer</div>
        {suppliers.length === 0 ? (
          <div style={{ fontSize: 11, color: "#fbbf24" }}>
            No suppliers yet — create one in the <strong>Suppliers</strong> tab first.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 4 }}>
            <RequiredLegend />
            <Row label="Supplier" required>
              <SearchableSelect
                options={supplierOptions}
                value={newOffer.supplier_id}
                onChange={(v) => setNewOffer({ ...newOffer, supplier_id: v })}
                placeholder="search supplier..."
              />
            </Row>
            <Row label="Rate" required hint="price per base unit">
              <div style={{ display: "flex", gap: 4 }}>
                <input type="number" placeholder="0.00" value={newOffer.unit_rate}
                  onChange={(e) => setNewOffer({ ...newOffer, unit_rate: e.target.value })}
                  style={{ flex: 1 }} />
                <input value={newOffer.currency}
                  onChange={(e) => setNewOffer({ ...newOffer, currency: e.target.value })}
                  style={{ width: 56 }} placeholder="GEL" />
              </div>
            </Row>
            <Row label="Valid from" hint="defaults to today">
              <input type="date" value={newOffer.valid_from}
                onChange={(e) => setNewOffer({ ...newOffer, valid_from: e.target.value })} />
            </Row>
            <Row label="Valid to" hint="leave blank = open-ended">
              <input type="date" value={newOffer.valid_to}
                onChange={(e) => setNewOffer({ ...newOffer, valid_to: e.target.value })} />
            </Row>
            <button className="primary" onClick={handleAdd}
              disabled={busy || !newOffer.supplier_id || !newOffer.unit_rate}
              style={{ marginTop: 4 }}>+ Add offer</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children, required = false, hint }) {
  return (
    <label style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 6, alignItems: "center", fontSize: 11 }}>
      <span style={{ color: required ? "#e2e8f0" : "#94a3b8" }}>
        {label}
        {required && <span style={{ color: "#f87171", marginLeft: 3 }}>*</span>}
        {hint && <span style={{ color: "#64748b", marginLeft: 4, fontSize: 10 }}>{hint}</span>}
      </span>
      {children}
    </label>
  );
}

// Small legend shown above each form
function RequiredLegend() {
  return (
    <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 6 }}>
      <span style={{ color: "#f87171" }}>*</span> required field
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// SUPPLIERS sub-panel
// ────────────────────────────────────────────────────────────────────

function SuppliersSubPanel({ token, canEdit, busy, setBusy, setStatus, setErr }) {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [newSup, setNewSup] = useState({ name: "", inn: "", contact_person: "", contact_email: "", contact_phone: "" });

  const load = useCallback(async () => {
    try {
      const data = await fetchSuppliers(token, { q: search });
      setList(data || []);
    } catch (e) {
      setErr(e?.message || "Failed to load suppliers");
    }
  }, [token, search, setErr]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newSup.name.trim()) return setErr("Name required");
    setBusy(true);
    try {
      await createSupplier(token, newSup);
      setAdding(false);
      setNewSup({ name: "", inn: "", contact_person: "", contact_email: "", contact_phone: "" });
      setStatus(`Supplier "${newSup.name}" added.`);
      load();
    } catch (e) {
      setErr(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (s) => {
    if (!(await confirmDialog(`Delete supplier "${s.name}" and all their offers?`))) return;
    setBusy(true);
    try {
      await deleteSupplier(token, s.id);
      load();
    } catch (e) {
      setErr(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const handleLoadExcel = async () => {
    setBusy(true);
    try {
      const all = await fetchSuppliers(token, {});
      const sheet = await writeSuppliersToExcel(all || []);
      setStatus(`Loaded ${(all || []).length} suppliers into "${sheet}". Edit, then "Save from Excel".`);
    } catch (e) {
      setErr(e?.message || "Failed");
    } finally { setBusy(false); }
  };

  const handleSaveExcel = async () => {
    if (!canEdit) return setErr("You have no edit access here.");
    setBusy(true);
    try {
      const rows = await readSuppliersFromExcel();
      if (!rows.length) return setErr("Suppliers sheet is empty.");
      const r = await bulkSuppliers(token, rows);
      setStatus(`Saved: +${r.written.created}/~${r.written.updated}${r.error_count ? ` (${r.error_count} errors)` : ""}.`);
      if (r.error_count) {
        const samples = (r.errors || []).slice(0, 3).map((e) => `row ${e.row}: ${e.error}`).join("; ");
        setErr(`${r.error_count} row(s) failed: ${samples}`);
      }
      load();
    } catch (e) {
      setErr(e?.message || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="panel" style={{ marginBottom: 8, padding: 8 }}>
        <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>
          Excel bulk edit — load all suppliers into one sheet, edit, save back.
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className="boq-action-btn" onClick={handleLoadExcel} disabled={busy}>↧ Load to Excel</button>
          {canEdit && (
            <button className="primary" onClick={handleSaveExcel} disabled={busy}>✓ Save from Excel</button>
          )}
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <input placeholder="search by name / INN..." value={search}
            onChange={(e) => setSearch(e.target.value)} style={{ flex: 1 }} />
          {canEdit && (
            <button className="primary" onClick={() => setAdding(!adding)} disabled={busy}>
              {adding ? "✕" : "+ Add supplier"}
            </button>
          )}
        </div>

        {adding && (
          <div style={{ display: "grid", gap: 4, marginTop: 8, padding: 8, border: "1px solid #334155", borderRadius: 4 }}>
            <RequiredLegend />
            <Row label="Name" required>
              <input value={newSup.name} onChange={(e) => setNewSup({ ...newSup, name: e.target.value })} />
            </Row>
            <Row label="INN / tax ID">
              <input value={newSup.inn} onChange={(e) => setNewSup({ ...newSup, inn: e.target.value })} />
            </Row>
            <Row label="Contact person">
              <input value={newSup.contact_person} onChange={(e) => setNewSup({ ...newSup, contact_person: e.target.value })} />
            </Row>
            <Row label="Email">
              <input value={newSup.contact_email} onChange={(e) => setNewSup({ ...newSup, contact_email: e.target.value })} />
            </Row>
            <Row label="Phone">
              <input value={newSup.contact_phone} onChange={(e) => setNewSup({ ...newSup, contact_phone: e.target.value })} />
            </Row>
            <button className="primary" onClick={handleAdd} disabled={busy || !newSup.name.trim()}>Save</button>
          </div>
        )}
      </div>

      <div className="panel">
        <div style={{ fontSize: 12, marginBottom: 6 }}>Suppliers ({list.length})</div>
        <div style={{ maxHeight: 400, overflow: "auto" }}>
          {list.length === 0 && <div style={{ color: "#94a3b8", fontSize: 11 }}>No suppliers yet.</div>}
          {list.map((s) => (
            <div key={s.id} style={{ display: "flex", padding: "4px 0", borderBottom: "1px solid #1e293b" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{s.name}</div>
                <div style={{ fontSize: 10, color: "#94a3b8" }}>
                  {[s.inn && `INN ${s.inn}`, s.contact_person, s.contact_email, s.contact_phone].filter(Boolean).join(" · ")}
                </div>
              </div>
              {canEdit && (
                <button className="boq-action-btn" style={{ padding: "2px 6px", fontSize: 11 }}
                  onClick={() => handleDelete(s)} disabled={busy}>✕</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// UNITS sub-panel
// ────────────────────────────────────────────────────────────────────

function UnitsSubPanel({ token, canEdit, busy, setBusy, setStatus, setErr }) {
  const [units, setUnits] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newUnit, setNewUnit] = useState({ code: "", dimension: "length", symbol: "", factor_to_base: 1, is_base: false });

  const load = useCallback(async () => {
    try {
      const list = await fetchUnits(token);
      setUnits(list || []);
    } catch (e) {
      setErr(e?.message || "Failed");
    }
  }, [token, setErr]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const map = {};
    units.forEach((u) => {
      const d = u.dimension || "—";
      if (!map[d]) map[d] = [];
      map[d].push(u);
    });
    return map;
  }, [units]);

  const handleAdd = async () => {
    if (!newUnit.code.trim() || !newUnit.dimension.trim()) return setErr("Code and dimension required");
    setBusy(true);
    try {
      await upsertUnit(token, {
        ...newUnit,
        factor_to_base: Number(newUnit.factor_to_base) || 1,
      });
      setAdding(false);
      setNewUnit({ code: "", dimension: "length", symbol: "", factor_to_base: 1, is_base: false });
      load();
      setStatus(`Unit ${newUnit.code} saved.`);
    } catch (e) {
      setErr(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const handleLoadExcel = async () => {
    setBusy(true);
    try {
      const sheet = await writeUnitsToExcel(units);
      setStatus(`Loaded ${units.length} units into "${sheet}". Edit, then "Save from Excel".`);
    } catch (e) {
      setErr(e?.message || "Failed");
    } finally { setBusy(false); }
  };

  const handleSaveExcel = async () => {
    if (!canEdit) return setErr("You have no edit access here.");
    setBusy(true);
    try {
      const rows = await readUnitsFromExcel();
      if (!rows.length) return setErr("Units sheet is empty.");
      const r = await bulkUnits(token, rows);
      setStatus(`Saved: +${r.written.created}/~${r.written.updated}${r.error_count ? ` (${r.error_count} errors)` : ""}.`);
      if (r.error_count) {
        const samples = (r.errors || []).slice(0, 3).map((e) => `row ${e.row}: ${e.error}`).join("; ");
        setErr(`${r.error_count} row(s) failed: ${samples}`);
      }
      load();
    } catch (e) {
      setErr(e?.message || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="panel" style={{ marginBottom: 8, padding: 8 }}>
        <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>
          Excel bulk edit — load all units, edit factors / names in Excel, save back.
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className="boq-action-btn" onClick={handleLoadExcel} disabled={busy}>↧ Load to Excel</button>
          {canEdit && (
            <button className="primary" onClick={handleSaveExcel} disabled={busy}>✓ Save from Excel</button>
          )}
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "#94a3b8" }}>{units.length} units · {Object.keys(grouped).length} dimensions</span>
          {canEdit && (
            <button className="primary" onClick={() => setAdding(!adding)} disabled={busy}>{adding ? "✕" : "+ Add unit"}</button>
          )}
        </div>

        {adding && (
          <div style={{ display: "grid", gap: 4, marginTop: 8, padding: 8, border: "1px solid #334155", borderRadius: 4 }}>
            <RequiredLegend />
            <Row label="Code" required hint="e.g. 'mm'">
              <input value={newUnit.code} onChange={(e) => setNewUnit({ ...newUnit, code: e.target.value })} />
            </Row>
            <Row label="Dimension" required hint="length, mass, volume, area, time…">
              <input value={newUnit.dimension} onChange={(e) => setNewUnit({ ...newUnit, dimension: e.target.value })} />
            </Row>
            <Row label="Factor to base" required hint="1=base, e.g. cm→m is 0.01">
              <input type="number" step="0.000001"
                value={newUnit.factor_to_base}
                onChange={(e) => setNewUnit({ ...newUnit, factor_to_base: e.target.value })} />
            </Row>
            <Row label="Symbol" hint="e.g. 'мм'">
              <input value={newUnit.symbol} onChange={(e) => setNewUnit({ ...newUnit, symbol: e.target.value })} />
            </Row>
            <Row label="Is base?" hint="check if this is the dimension's reference unit">
              <input type="checkbox" checked={newUnit.is_base} onChange={(e) => setNewUnit({ ...newUnit, is_base: e.target.checked })} />
            </Row>
            <button className="primary" onClick={handleAdd}
              disabled={busy || !newUnit.code.trim() || !newUnit.dimension.trim() || newUnit.factor_to_base === ""}>
              Save
            </button>
          </div>
        )}
      </div>

      <div className="panel">
        {Object.keys(grouped).sort().map((dim) => (
          <div key={dim} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: "#67e8f9", marginBottom: 2 }}>{dim}</div>
            {grouped[dim].map((u) => (
              <div key={u.code} style={{ display: "flex", padding: "2px 6px", fontSize: 11, borderBottom: "1px solid #1e293b" }}>
                <span style={{ width: 80, fontFamily: "monospace" }}>{u.code}</span>
                <span style={{ width: 60 }}>{u.symbol}</span>
                <span style={{ flex: 1, color: "#94a3b8" }}>{u.name_geo || u.name_eng}</span>
                <span style={{ width: 110, textAlign: "right", color: "#94a3b8" }}>
                  ×{u.factor_to_base}{u.is_base && " (base)"}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// OFFERS / PRICES sub-panel (Excel bulk + brief overview)
// ────────────────────────────────────────────────────────────────────

function OffersSubPanel({ token, canEdit, busy, setBusy, setStatus, setErr }) {
  const [offers, setOffers] = useState([]);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null); // offer id, or "new"
  const [form, setForm] = useState({ supplier_id: "", product_id: "", unit_rate: "", currency: "GEL" });
  const [formSuppliers, setFormSuppliers] = useState([]);
  const [formProducts, setFormProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await fetchOffers(token, { activeOn: new Date().toISOString().slice(0, 10) });
      setOffers(list || []);
    } catch (e) {
      setErr(e?.message || "Failed to load offers");
    }
  }, [token, setErr]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetchSuppliers(token, { isActive: true }).then(setFormSuppliers).catch(() => setFormSuppliers([]));
  }, [token]);

  useEffect(() => {
    if (editingId !== "new") return;
    const t = setTimeout(() => {
      fetchProducts(token, { q: productSearch, limit: 120 })
        .then((list) => setFormProducts(list || []))
        .catch(() => setFormProducts([]));
    }, 200);
    return () => clearTimeout(t);
  }, [token, productSearch, editingId]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return offers;
    return offers.filter((o) =>
      (o.supplier_name || "").toLowerCase().includes(q) ||
      (o.product_display_id || "").toLowerCase().includes(q) ||
      (o.product_name_geo || "").toLowerCase().includes(q) ||
      (o.product_name_eng || "").toLowerCase().includes(q)
    );
  }, [offers, search]);

  const handleSetPreferred = async (offer) => {
    if (offer.is_preferred) return;
    setBusy(true);
    try {
      await updateOffer(token, offer.id, { is_preferred: true });
      load();
    } catch (e) {
      setErr(e?.message || "Failed to set preferred");
    } finally { setBusy(false); }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog("Delete this offer? Price history is kept."))) return;
    setBusy(true);
    try {
      await deleteOffer(token, id);
      load();
    } catch (e) {
      setErr(e?.message || "Failed to delete");
    } finally { setBusy(false); }
  };

  const startEdit = (offer) => {
    setForm({
      supplier_id: String(offer.supplier_id),
      product_id: String(offer.product_id),
      unit_rate: String(offer.unit_rate),
      currency: offer.currency || "GEL",
    });
    setEditingId(offer.id);
  };

  const startAdd = () => {
    setForm({ supplier_id: "", product_id: "", unit_rate: "", currency: "GEL" });
    setProductSearch("");
    setEditingId("new");
  };

  const cancelForm = () => { setEditingId(null); setForm({ supplier_id: "", product_id: "", unit_rate: "", currency: "GEL" }); };

  const handleSave = async () => {
    if (!form.unit_rate) return setErr("Rate is required.");
    if (editingId === "new" && (!form.supplier_id || !form.product_id)) return setErr("Supplier and product are required.");
    setSaving(true);
    try {
      if (editingId === "new") {
        await createOffer(token, {
          supplier_id: Number(form.supplier_id),
          product_id: Number(form.product_id),
          unit_rate: Number(form.unit_rate),
          currency: form.currency || "GEL",
        });
        setStatus("Offer added.");
      } else {
        await updateOffer(token, editingId, {
          unit_rate: Number(form.unit_rate),
          currency: form.currency || "GEL",
        });
        setStatus("Offer updated.");
      }
      cancelForm();
      load();
    } catch (e) {
      setErr(e?.message || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleLoadExcel = async () => {
    setBusy(true);
    try {
      const sheet = await writeOffersToExcel(offers);
      setStatus(`Loaded ${offers.length} active offer(s) into "${sheet}". Edit rows, save back.`);
    } catch (e) {
      setErr(e?.message || "Failed");
    } finally { setBusy(false); }
  };

  const handleSaveExcel = async () => {
    if (!canEdit) return setErr("You have no edit access here.");
    setBusy(true);
    try {
      const rows = await readOffersFromExcel();
      if (!rows.length) return setErr("Offers sheet is empty.");
      const r = await bulkOffers(token, rows);
      const expired = r.written?.expired || 0;
      setStatus(
        `Saved: +${r.written.created}/~${r.written.updated}` +
        (expired > 0 ? ` · ${expired} expired` : "") +
        (r.error_count ? ` · ${r.error_count} errors` : "") + "."
      );
      if (r.error_count) {
        const samples = (r.errors || []).slice(0, 3).map((e) => `row ${e.row}: ${e.error}`).join("; ");
        setErr(`${r.error_count} row(s) failed: ${samples}`);
      }
      load();
    } catch (e) {
      setErr(e?.message || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

      {/* Excel bulk section */}
      <div className="panel" style={{ padding: 8 }}>
        <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 6 }}>
          Excel bulk edit — load all active prices, edit / add rows, save back.
          Deleted rows get <strong>expired</strong> (history preserved).
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="boq-action-btn" onClick={handleLoadExcel} disabled={busy}>
            ↧ Load to Excel
          </button>
          {canEdit && (
            <button className="primary" onClick={handleSaveExcel} disabled={busy}>
              ✓ Save from Excel
            </button>
          )}
        </div>
      </div>

      {/* Search + list */}
      <div className="panel" style={{ padding: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <input
            placeholder="search supplier, product code or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, fontSize: 11 }}
          />
          <span style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>
            {filtered.length}/{offers.length}
          </span>
          {canEdit && editingId === null && (
            <button className="primary" style={{ padding: "3px 10px", fontSize: 11 }}
              onClick={startAdd} disabled={busy}>
              + Add
            </button>
          )}
        </div>

        {/* Add / edit form */}
        {editingId !== null && canEdit && (
          <div style={{
            marginBottom: 8, padding: 10, border: "1px solid #14b8a6",
            borderRadius: 4, display: "grid", gap: 6,
          }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>
              {editingId === "new" ? "New price offer" : `Edit offer #${editingId}`}
            </div>

            {editingId === "new" && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: 4, alignItems: "center", fontSize: 11 }}>
                  <span style={{ color: "#94a3b8" }}>Product</span>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <input placeholder="search product…" value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)} style={{ fontSize: 11 }} />
                    <select value={form.product_id}
                      onChange={(e) => setForm({ ...form, product_id: e.target.value })}
                      style={{ fontSize: 11 }}>
                      <option value="">— pick product —</option>
                      {formProducts.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.display_id || `#${p.id}`} — {p.name_geo || p.name_eng || ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: 4, alignItems: "center", fontSize: 11 }}>
                  <span style={{ color: "#94a3b8" }}>Supplier</span>
                  <SearchableSelect
                    options={formSuppliers.map((s) => ({ value: String(s.id), label: s.name }))}
                    value={form.supplier_id}
                    onChange={(v) => setForm({ ...form, supplier_id: v })}
                    placeholder="search supplier…"
                  />
                </div>
              </>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "90px 1fr 60px", gap: 4, alignItems: "center", fontSize: 11 }}>
              <span style={{ color: "#94a3b8" }}>Rate</span>
              <input type="number" step="0.01" placeholder="0.00"
                value={form.unit_rate}
                onChange={(e) => setForm({ ...form, unit_rate: e.target.value })} />
              <input value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                placeholder="GEL" style={{ fontSize: 11 }} />
            </div>

            {editingId !== "new" && (
              <div style={{ fontSize: 10, color: "#94a3b8" }}>
                Note: direct edit overwrites rate without preserving history.
                To preserve history, use "update price" inside the Catalog tab.
              </div>
            )}

            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <button className="boq-action-btn" onClick={cancelForm} disabled={saving}>Cancel</button>
              <button className="primary" onClick={handleSave} disabled={saving || !form.unit_rate}>
                {saving ? "…" : editingId === "new" ? "Add offer" : "Save changes"}
              </button>
            </div>
          </div>
        )}

        {/* Offers list */}
        <div style={{ maxHeight: 380, overflowY: "auto" }}>
          {filtered.length === 0 && (
            <div style={{ color: "#94a3b8", fontSize: 11, padding: "6px 0" }}>
              {offers.length === 0 ? "No active supplier offers yet." : "No results for current search."}
            </div>
          )}
          {filtered.map((o) => (
            <div key={o.id} style={{
              display: "grid",
              gridTemplateColumns: "16px minmax(0,1.2fr) minmax(0,1.6fr) 90px 74px" + (canEdit ? " auto" : ""),
              alignItems: "center",
              padding: "4px 4px",
              borderBottom: "1px solid #1e293b",
              fontSize: 11,
              gap: 6,
            }}>
              {/* Star */}
              {canEdit
                ? <button onClick={() => handleSetPreferred(o)} disabled={busy || o.is_preferred}
                    title={o.is_preferred ? "Default price" : "Set as default"}
                    style={{ background: "none", border: "none", cursor: o.is_preferred ? "default" : "pointer",
                             padding: 0, color: o.is_preferred ? "#34d399" : "#475569", fontSize: 13, lineHeight: 1 }}>★</button>
                : <span style={{ color: o.is_preferred ? "#34d399" : "transparent", fontSize: 13 }}>★</span>
              }

              {/* Supplier */}
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#e2e8f0" }}>
                {o.supplier_name || "—"}
              </span>

              {/* Product */}
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#94a3b8" }}>
                <span style={{ fontFamily: "monospace", color: "#67e8f9", marginRight: 4 }}>
                  {o.product_display_id || `#${o.product_id}`}
                </span>
                {o.product_name_geo || o.product_name_eng || ""}
              </span>

              {/* Rate */}
              <span style={{ textAlign: "right", fontWeight: 600, color: "#f0abfc" }}>
                {o.unit_rate} <span style={{ color: "#94a3b8", fontWeight: 400 }}>{o.currency}</span>
              </span>

              {/* Since */}
              <span style={{ fontSize: 10, color: "#64748b", textAlign: "right" }}>
                {o.valid_from}
              </span>

              {/* Actions */}
              {canEdit && (
                <span style={{ display: "flex", gap: 3 }}>
                  <button className="boq-action-btn" style={{ padding: "1px 6px", fontSize: 10 }}
                    onClick={() => startEdit(o)} disabled={busy || editingId !== null}>edit</button>
                  <button className="boq-action-btn" style={{ padding: "1px 6px", fontSize: 10 }}
                    onClick={() => handleDelete(o.id)} disabled={busy}>✕</button>
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
